import {
  Button,
  Combobox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  MessageBar,
  MessageBarBody,
  Option,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { DeleteRegular } from "@fluentui/react-icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@skanska/vsa-client-auth";
import {
  useAddEntraGroupMember,
  useEntraGroupMembers,
  useRemoveEntraGroupMember,
  useSearchEntraUsers,
} from "../hooks/useEntraGroupMembership";
import {
  entraGroups,
  getEntraGroupFromUrlKey,
  getEntraGroupKeyFromSearchParams,
  EntraGroupMember,
  EntraUser,
} from "../models/EntraGroupMembership";
import { SkanskaColorTokens } from "../style/colors";

const useStyles = makeStyles({
  root: {
    width: "100%",
    maxWidth: "720px",
    boxSizing: "border-box",
    margin: "0 auto",
    padding: tokens.spacingVerticalXL,
  },
  form: {
    display: "grid",
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalL,
    backgroundColor: SkanskaColorTokens.color_secondary_dim_white,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
  },
  members: {
    width: "100%",
    marginTop: tokens.spacingVerticalXL,
  },
  membersTable: {
    width: "100%",
  },
  memberEmail: {
    overflowWrap: "anywhere",
  },
  removeButton: {
    color: SkanskaColorTokens.color_action_red,
  },
});

function EntraGroupMembershipPage() {
  const classes = useStyles();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState<EntraUser | null>(null);
  const [memberToRemove, setMemberToRemove] = useState<EntraGroupMember | null>(
    null
  );
  const [feedback, setFeedback] = useState<
    | "success"
    | "error"
    | "conflict"
    | "removeSuccess"
    | "removeError"
    | "removeStale"
    | null
  >(null);
  const selectedGroupKey = getEntraGroupKeyFromSearchParams(searchParams);
  const users = useSearchEntraUsers(searchTerm);
  const members = useEntraGroupMembers(selectedGroupKey);
  const addMember = useAddEntraGroupMember();
  const removeMember = useRemoveEntraGroupMember();

  const submit = async () => {
    if (selectedGroupKey === null || selectedUser === null) return;

    setFeedback(null);
    try {
      const addedMember = await addMember.mutateAsync({
        groupKey: selectedGroupKey,
        userObjectId: selectedUser.id,
      });
      const memberQueryKey = ["entra-group-members", selectedGroupKey] as const;
      queryClient.setQueryData<EntraGroupMember[]>(
        memberQueryKey,
        (currentMembers = []) => {
          const memberIndex = currentMembers.findIndex(
            (member) => member.id === addedMember.id
          );

          if (memberIndex === -1) {
            return [...currentMembers, addedMember];
          }

          return currentMembers.map((member, index) =>
            index === memberIndex ? addedMember : member
          );
        }
      );
      setFeedback("success");
      setSelectedUser(null);
      setSearchTerm("");
    } catch (error) {
      const status = (error as { status?: number }).status;
      setFeedback(status === 409 ? "conflict" : "error");
    }
  };

  const removeSelectedMember = async () => {
    if (selectedGroupKey === null || memberToRemove === null) return;

    const groupKey = selectedGroupKey;
    const memberId = memberToRemove.id;
    setFeedback(null);
    try {
      await removeMember.mutateAsync({
        groupKey,
        memberObjectId: memberId,
      });
      const memberQueryKey = ["entra-group-members", groupKey] as const;
      queryClient.setQueryData<EntraGroupMember[]>(
        memberQueryKey,
        (currentMembers = []) =>
          currentMembers.filter((member) => member.id !== memberId)
      );
      setFeedback("removeSuccess");
    } catch (error) {
      const status = (error as { status?: number }).status;
      setFeedback(status === 404 ? "removeStale" : "removeError");
    } finally {
      setMemberToRemove(null);
    }
  };

  const selectedGroupOption = entraGroups.find(
    (option) => option.key === selectedGroupKey
  );

  const selectGroup = (urlKey: string | undefined) => {
    const selectedGroup = getEntraGroupFromUrlKey(urlKey ?? null);

    if (selectedGroup === null) return;

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("group", selectedGroup.urlKey);
    setSearchParams(nextSearchParams);
  };

  return (
    <section className={classes.root}>
      <h1>{t("entraGroupMembership.title")}</h1>
      <div className={classes.form}>
        {feedback === "success" && (
          <MessageBar intent="success">
            <MessageBarBody>{t("entraGroupMembership.success")}</MessageBarBody>
          </MessageBar>
        )}
        {feedback === "conflict" && (
          <MessageBar intent="warning">
            <MessageBarBody>
              {t("entraGroupMembership.alreadyMember")}
            </MessageBarBody>
          </MessageBar>
        )}
        {feedback === "error" && (
          <MessageBar intent="error">
            <MessageBarBody>{t("entraGroupMembership.error")}</MessageBarBody>
          </MessageBar>
        )}
        {feedback === "removeSuccess" && (
          <MessageBar intent="success">
            <MessageBarBody>
              {t("entraGroupMembership.removeSuccess")}
            </MessageBarBody>
          </MessageBar>
        )}
        {feedback === "removeError" && (
          <MessageBar intent="error">
            <MessageBarBody>
              {t("entraGroupMembership.removeError")}
            </MessageBarBody>
          </MessageBar>
        )}
        {feedback === "removeStale" && (
          <MessageBar intent="warning">
            <MessageBarBody>
              {t("entraGroupMembership.removeStale")}
            </MessageBarBody>
          </MessageBar>
        )}
        <Field label={t("entraGroupMembership.groupLabel")} required>
          <Combobox
            placeholder={t("entraGroupMembership.groupPlaceholder")}
            selectedOptions={
              selectedGroupOption === undefined
                ? []
                : [selectedGroupOption.urlKey]
            }
            value={
              selectedGroupOption
                ? t(
                    `entraGroupMembership.groups.${selectedGroupOption.translationKey}`
                  )
                : ""
            }
            onOptionSelect={(_, data) => selectGroup(data.optionValue)}
          >
            {entraGroups.map((option) => (
              <Option key={option.key} value={option.urlKey}>
                {t(`entraGroupMembership.groups.${option.translationKey}`)}
              </Option>
            ))}
          </Combobox>
        </Field>
        <Field
          label={t("entraGroupMembership.userLabel")}
          hint={t("entraGroupMembership.userHint")}
          required
        >
          <Combobox
            freeform
            placeholder={t("entraGroupMembership.userPlaceholder")}
            selectedOptions={selectedUser ? [selectedUser.id] : []}
            value={selectedUser ? selectedUser.displayName : searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              setSelectedUser(null);
            }}
            onOptionSelect={(_, data) => {
              const user = users.data?.find(
                (item) => item.id === data.optionValue
              );
              setSelectedUser(user ?? null);
              if (user) setSearchTerm(user.displayName);
            }}
          >
            {users.isLoading && <Spinner size="tiny" />}
            {users.data?.map((user) => (
              <Option key={user.id} value={user.id} text={user.displayName}>
                {user.email
                  ? `${user.displayName} (${user.email})`
                  : user.displayName}
              </Option>
            ))}
          </Combobox>
        </Field>
        <div className={classes.actions}>
          <Button
            appearance="primary"
            disabled={
              selectedGroupKey === null ||
              selectedUser === null ||
              addMember.isPending
            }
            onClick={submit}
          >
            {t("entraGroupMembership.add")}
          </Button>
        </div>
      </div>
      {selectedGroupKey !== null && (
        <section className={classes.members}>
          <h2>{t("entraGroupMembership.membersTitle")}</h2>
          {members.isLoading && (
            <Spinner label={t("entraGroupMembership.membersLoading")} />
          )}
          {members.error && (
            <MessageBar intent="error">
              <MessageBarBody>
                {t("entraGroupMembership.membersError")}
              </MessageBarBody>
            </MessageBar>
          )}
          {members.data?.length === 0 && (
            <p>{t("entraGroupMembership.membersEmpty")}</p>
          )}
          {members.data && members.data.length > 0 && (
            <Table
              aria-label={t("entraGroupMembership.membersTitle")}
              className={classes.membersTable}
            >
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>
                    {t("entraGroupMembership.memberName")}
                  </TableHeaderCell>
                  <TableHeaderCell>
                    {t("entraGroupMembership.memberEmail")}
                  </TableHeaderCell>
                  <TableHeaderCell>
                    {t("entraGroupMembership.memberActions")}
                  </TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.data.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>{member.displayName}</TableCell>
                    <TableCell className={classes.memberEmail}>
                      {member.email ?? "-"}
                    </TableCell>
                    <TableCell>
                      <Button
                        appearance="transparent"
                        aria-label={t("entraGroupMembership.removeMember")}
                        title={t("entraGroupMembership.removeMember")}
                        icon={<DeleteRegular />}
                        className={classes.removeButton}
                        onClick={() => setMemberToRemove(member)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </section>
      )}
      <Dialog open={memberToRemove !== null}>
        <DialogSurface aria-describedby={undefined}>
          <DialogBody>
            <DialogTitle>
              {t("entraGroupMembership.removeMemberTitle")}
            </DialogTitle>
            <DialogContent>
              {t("entraGroupMembership.removeMemberPrompt", {
                name: memberToRemove?.displayName,
              })}
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button
                  onClick={() => setMemberToRemove(null)}
                  appearance="secondary"
                >
                  {t("cancel")}
                </Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                disabled={removeMember.isPending}
                onClick={removeSelectedMember}
              >
                {t("entraGroupMembership.remove")}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </section>
  );
}

export default EntraGroupMembershipPage;
